import { SearchableSelect } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { UserGeneralInfoContextKeys } from "../../enums/userGeneralInfoContextKeys";
import { useOrderAssignmentByAuthStatus } from "../../hooks/useOrderAssignmentByAuthStatus";
import { UserStore } from "../../store/UserGeneralInfoStore";
import CustomCheckBox from "../customCheckBox/customCheckBox";

/** Represents a provider selection emitted from the reassignment dropdown. */
interface SelectedProvider {
  id: string;
  name: string;
}

/** Props for {@link OrderReassignmentWithCheckbox}. */
interface OrderReassignmentWithCheckboxProps {
  onSelectedProviderChange?: (provider: SelectedProvider) => void;
  /** Fires when the reassignment checkbox is toggled so parents can gate save actions. */
  onReassignmentCheckedChange?: (checked: boolean) => void;
}

/**
 * Renders a checkbox that conditionally reveals a searchable provider selector
 * used to reassign the order during mark-as-completed flow.
 *
 * @param props - Callback for selected provider updates
 * @returns Reassignment checkbox and searchable provider dropdown
 */
const OrderReassignmentWithCheckbox = (
  props: OrderReassignmentWithCheckboxProps,
) => {
  const { configurations, loading, fetchConfigurations } =
    useOrderAssignmentByAuthStatus();
  const [checked, setChecked] = useState<boolean>(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");

  useEffect(() => {
    if (checked) {
      fetchConfigurations(
        UserStore.getItem(UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID),
        MedicalPaOrdersAuthStatus.AuthRequired,
      );
    }
  }, [checked]);

  const generateOptions = useCallback(() => {
    return configurations.map((configuration) => ({
      value: configuration.provider_id,
      label: configuration.provider_name,
    }));
  }, [configurations]);

  return (
    <div className="flex w-full flex-col gap-3 border-t border-primaryGray-15 pt-3">
      <CustomCheckBox
        id="reassign-order-on-mark-as-complete"
        label="Reassign order on mark as completed"
        onCheckBoxValueChange={(value) => {
          const next = value.value;
          setChecked(next);
          setSelectedProviderId("");
          props.onSelectedProviderChange?.({ id: "", name: "" });
          props.onReassignmentCheckedChange?.(next);
        }}
        className="!mt-[3px] text-sm"
        isChecked={checked}
      />
      {checked &&
        (loading ? (
          <Loading />
        ) : (
          <SearchableSelect
            id="order-reassignment-provider"
            options={generateOptions()}
            value={selectedProviderId}
            placeholder="Select a provider"
            searchPlaceholder="Search providers"
            label=""
            onOptionChange={(option) => {
              setSelectedProviderId(option.value);
              // Resolve the canonical provider_name from `configurations` by
              // provider_id rather than reading `option.name`. The
              // SearchableSelect option only carries `value` and `label`,
              // and `option.name` is undefined — using it here would emit
              // an empty/incorrect provider name to the parent.
              props.onSelectedProviderChange?.({
                id: option.value,
                name:
                  configurations.find((c) => c.provider_id === option.value)
                    ?.provider_name ?? "",
              });
            }}
          />
        ))}
    </div>
  );
};

export default OrderReassignmentWithCheckbox;

const Loading = () => {
  return (
    <div className="flex h-full items-center justify-center gap-4">
      <SpinningLoader />
      <span className="text-small text-primaryGray-2">
        Loading providers...
      </span>
    </div>
  );
};
